import { useMemo, useState } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

type PayableDocumentViewerProps = {
  docUrl: string
  s3Path?: string | null
}

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.25

const isImageDocument = (docUrl: string, s3Path?: string | null) => {
  const source = `${s3Path ?? ''} ${docUrl}`.toLowerCase()
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/.test(source)
}

const PayableDocumentViewer = ({ docUrl, s3Path }: PayableDocumentViewerProps) => {
  const [scale, setScale] = useState(1)
  const isImage = useMemo(
    () => isImageDocument(docUrl, s3Path),
    [docUrl, s3Path]
  )

  const zoomOut = () =>
    setScale((prev) => Math.max(MIN_SCALE, Number((prev - SCALE_STEP).toFixed(2))))
  const zoomIn = () =>
    setScale((prev) => Math.min(MAX_SCALE, Number((prev + SCALE_STEP).toFixed(2))))

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 480,
        bgcolor: '#4a4a4a',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ px: 1, py: 0.5, bgcolor: '#3a3a3a', flexShrink: 0 }}
      >
        <IconButton
          size="small"
          onClick={zoomOut}
          disabled={scale <= MIN_SCALE}
          sx={{ color: 'common.white' }}
          aria-label="Zoom out"
        >
          <ZoomOutIcon />
        </IconButton>
        <Typography variant="body2" sx={{ color: 'common.white', minWidth: 48, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton
          size="small"
          onClick={zoomIn}
          disabled={scale >= MAX_SCALE}
          sx={{ color: 'common.white' }}
          aria-label="Zoom in"
        >
          <ZoomInIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 1,
        }}
      >
        {isImage ? (
          <Box
            component="img"
            src={docUrl}
            alt="Expense document"
            sx={{
              width: `${scale * 100}%`,
              maxWidth: 'none',
              height: 'auto',
              display: 'block',
              bgcolor: 'common.white',
            }}
          />
        ) : (
          <Box
            sx={{
              width: `${scale * 100}%`,
              minWidth: '100%',
              height: `${Math.max(scale, 1) * 100}%`,
              minHeight: '100%',
            }}
          >
            <iframe
              src={docUrl}
              title="Expense document"
              width="100%"
              height="100%"
              style={{ border: 'none', background: '#fff', minHeight: 640 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default PayableDocumentViewer
